import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarTabs } from "./SidebarTabs";
import { SidebarDivider } from "./SidebarDivider";
import { SidebarSection } from "./SidebarSection";
import { AppBanner } from "./AppBanner";
import { SidebarHeader } from "./SidebarHeader";
import { SidebarLanguageSwitcher } from "./SidebarLanguageSwitcher";
import { AccountMenu, ProfileMenu } from "./menu";
import { ConfirmationModal } from "../../ui/modals";
import { GamesMenu } from "./menu/GamesMenu";
import { Button } from "../../ui";
import { ChatSupportMenu } from "./ChatSupportMenu";
import localization from "../../../config/localization";
import { localStorageService } from "../../../services";
import { useSelector } from "react-redux";
import { selectUserProfile } from "../../../features/users/selectors/userProfileSelectors";
import { sidebarMenu } from "./sidebarMenuConst";
import userDeviceStore from "../../../store/device.store";
import { DeviceType } from "../../../models/constants/deviceType";
import { AccessMode } from "../../../models/constants/accessMode";
import { getAvatarById } from "../../../features/users/components/avatar/avatar.config";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [activeTab, setActiveTab] = useState<string>(sidebarMenu.Account);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const profile = useSelector(selectUserProfile);

  const deviceType = userDeviceStore((state) => state.deviceType);
  const accessModeSource = userDeviceStore((state) => state.accessModeSource);
  const isAuthenticated =
    localStorageService.getItem("isUserLoggedIn") === true;

  useEffect(() => {
    const mainContent = document.getElementById("main-content");
    const userTabs = document.getElementById("user-tabs");

    if (!mainContent) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const blockedKeys = [
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
      ];

      if (blockedKeys.includes(e.key)) {
        e.preventDefault();
      }
    };

    if (isOpen) {
      userTabs?.classList.remove("sticky");

      mainContent.classList.add(
        "overflow-hidden",
        "max-h-[calc(100vh-100px)]",
        "pointer-events-none"
      );
      mainContent.style.backdropFilter = "blur(100px)";
      mainContent.style.opacity = "0.3";

      window.addEventListener("keydown", handleKeyDown, {
        passive: false,
      });
    } else {
      userTabs?.classList.add("sticky");

      mainContent.classList.remove(
        "overflow-hidden",
        "max-h-[calc(100vh-100px)]",
        "pointer-events-none"
      );
      mainContent.style.backdropFilter = "";
      mainContent.style.opacity = "";
      window.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      userTabs?.classList.add("sticky");
      mainContent.classList.remove(
        "overflow-hidden",
        "max-h-[calc(100vh-100px)]",
        "pointer-events-none"
      );
      mainContent.style.backdropFilter = "";
      mainContent.style.opacity = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isModalOpen) return;

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, isModalOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setActiveTab(sidebarMenu.Account);
    setOpenAccordion(null);

    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: 0 });
    });
  }, [isOpen]);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    localStorageService.clear();
    navigate("/");
    window.location.reload();
    onClose();
  };

  const handleTabChange = (tab: string) => {
    if (tab === sidebarMenu.Promotion) {
      navigate("/promotion");
      onClose();
    } else {
      setActiveTab(tab);
    }
  };

  let content = null;

  switch (activeTab) {
    case sidebarMenu.Account:
      content = (
        <AccountMenu
          openAccordion={openAccordion}
          toggleAccordion={toggleAccordion}
          handleNavigate={handleNavigate}
          isAuthenticated={isAuthenticated}
        />
      );
      break;

    case sidebarMenu.Profile:
      content = <ProfileMenu handleNavigate={handleNavigate} />;
      break;

    case sidebarMenu.Games:
      content = (
        <GamesMenu onClose={onClose} isAuthenticated={isAuthenticated} />
      );
      break;
  }

  const getAvatarSrc = () => {
    if (!profile?.profilePictureUrl) {
      return "/icons/ic-profile.svg";
    }
    else if (getAvatarById(profile?.profilePictureUrl)) {
      return getAvatarById(profile?.profilePictureUrl) ?? "/icons/ic-profile.svg";
    }
    else {
      return `${BLOB_IMAGE_URL}${BlobContainerConsts.UserAvatar}/${profile?.profilePictureUrl}`;
    }
  };

  const userInfo = isAuthenticated ? (
    <div
      className="mx-4 mt-5 mb-4 border border-[#2E2E2E]
                 bg-[#0F0F0F] rounded-xl p-4
                 flex justify-between items-center
                 cursor-pointer hover:bg-[#1f1f1f]"      
    >
      <div className="flex items-center gap-3"
      onClick={() => handleNavigate("/profile")}>
        <img
          src={getAvatarSrc()}
          alt="profile"
          className="w-10 h-10 rounded-full"
        />
        <span className="font-medium text-white">
          {profile?.name || profile?.username}
        </span>
      </div>

      <Button
        className="bg-primary-gradient px-4 py-2 rounded-lg"
        onClick={() => handleNavigate("/deposit")}
      >
        {localization.start_deposit}
      </Button>
    </div>
  ) : (
    <div
      className="mx-4 mt-5 mb-4 border border-[#2E2E2E]
                 bg-[#0F0F0F] rounded-xl px-4 py-3
                 flex items-center justify-between
                 hover:bg-[#1f1f1f] cursor-pointer"
    >
      <div className="flex items-center gap-3">
        <img
          src="/images/img-register.svg"
          alt="user"
          className="w-16 h-16 rounded-full"
        />
        <div className="flex flex-col leading-tight">
          <span className="font-semibold text-white">
            {localization.hey_there}
          </span>
          <span className="text-sm text-gray-400">
            {localization.start_journey}
          </span>
        </div>
      </div>

      <Button
        className="bg-primary-gradient px-4 py-2 rounded-lg"
        onClick={() => handleNavigate("/signup")}
      >
        {localization.register}
      </Button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <div
        className="!fixed inset-0 bg-black/50 z-40 lg:hidden"
        onClick={onClose}
      />

      <aside
        ref={sidebarRef}
        className={`
          !fixed inset-y-0 left-0 z-50 bg-[#151515]
          flex flex-col h-screen
          w-full sm:w-[80%] md:w-[60%] lg:w-[40%] xl:w-[30%]
          transform transition-transform duration-300
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {deviceType !== DeviceType.Web && (
          <SidebarHeader
            onClose={onClose}
            handleNavigate={handleNavigate}
            isAuthenticated={isAuthenticated}
          />
        )}

        <SidebarTabs activeTab={activeTab} onTabChange={handleTabChange} />
        <SidebarDivider />

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto pb-24 scrollbar-hide"
        >
          {activeTab !== sidebarMenu.Games && userInfo}
          {content}
          <ChatSupportMenu />

          {accessModeSource.sourceType === AccessMode.Web && (
            <AppBanner handleNavigate={handleNavigate} />
          )}

          {isAuthenticated && (
            <div className="mx-4 mt-4 space-y-3">
              <SidebarSection
                imgSrc="/icons/ic-logout.svg"
                label={localization.logout}
                onClick={() => setIsModalOpen(true)}
              />
            </div>
          )}
        </div>

        <SidebarLanguageSwitcher isAuthenticated={isAuthenticated} />

        {isModalOpen && (
          <ConfirmationModal
            isOpen={isModalOpen}
            title={localization.logout_confirmation}
            icon={"/icons/ic-info.svg"}
            blurColor="bg-[#FFE6CC]"
            confirmText="Yes"
            cancelText="No"
            onConfirm={handleLogout}
            onCancel={() => setIsModalOpen(false)}
          />
        )}
      </aside>
    </>
  );
}